import { isValidTimeZone } from '@shared/calendar';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { AlarmController } from '@/components/alarms/AlarmController';
import { NotesReviewNotifier } from '@/components/notes/NotesReviewNotifier';
import { CheckinAlertsNotifier } from '@/components/alarms/CheckinAlertsNotifier';
import { ShiftNoticeToast } from '@/components/shifts/ShiftNoticeToast';
import { CheckinController } from '@/components/timer/CheckinController';
import { TimerActionsProvider } from '@/components/timer/TimerActionsProvider';
import { ME_QUERY_KEY, useMe } from '@/hooks/use-me';
import { apiRequest } from '@/lib/api';
import { useSyncListener } from '@/lib/sync';
import type { MeResponse } from '@/types/me';
import { BottomTabBar } from './BottomTabBar';
import { GlobalShortcuts } from './GlobalShortcuts';
import { MobileHeader } from './MobileHeader';
import { navItemsFor } from './nav-items';
import { Sidebar } from './Sidebar';

function browserTimeZone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && isValidTimeZone(tz) ? tz : null;
  } catch {
    return null;
  }
}

/**
 * One-time timezone auto-detect (architecture.md F9): runs once per page load when the
 * server has never recorded a timezone for this user. Failures are silent by design.
 */
function useTimezoneAutoDetect(timezoneSetAt: string | null | undefined): void {
  const queryClient = useQueryClient();
  const attempted = useRef(false);
  const { mutate } = useMutation({
    mutationFn: (timezone: string) =>
      apiRequest<MeResponse>('/api/me/timezone', { method: 'POST', body: JSON.stringify({ timezone }) }),
    meta: { silent: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });

  useEffect(() => {
    if (timezoneSetAt !== null || attempted.current) return;
    const tz = browserTimeZone();
    if (!tz) return;
    attempted.current = true;
    mutate(tz);
  }, [timezoneSetAt, mutate]);
}

export function AppShell(): JSX.Element {
  const { data: me } = useMe();
  const items = navItemsFor(me);
  useTimezoneAutoDetect(me ? me.timezoneSetAt : undefined);
  useSyncListener();

  return (
    <div className="min-h-screen bg-background">
      {/* One set of timer dialogs and one stop flow for the indicators, pages, quick add and check-ins
          (sprint 4 §5.4). The sidebar sits inside because its timer indicator calls useTimerActions. */}
      <TimerActionsProvider>
        <Sidebar items={items} me={me} />
        <MobileHeader items={items} me={me} />
        {/* Original breakpoints (owner decision 5): sidebar lg+, header + drawer below lg, bottom bar below
            sm. No desktop top bar (owner decision 4). Pages pad themselves through <Page>. */}
        <main className="min-h-screen pb-[calc(3rem+env(safe-area-inset-bottom))] pt-12 sm:pb-0 lg:pl-56 lg:pt-0">
          <Outlet />
        </main>
        <CheckinController />
        {/* Sprint 14: check-in alarms on alarm shifts, and Supervisors' missed check-in notifications. */}
        <AlarmController />
        {me?.access.checkinNotify && <CheckinAlertsNotifier />}
        {/* Sprint 15: Supervisors' shift-end notes review notifications. */}
        {me?.access.notesReviewNotify && <NotesReviewNotifier />}
        {/* Sprint 13: the once-per-shift auto-close toast (from the shift that rides on GET /api/timer). */}
        <ShiftNoticeToast tz={me?.timezone} />
        <BottomTabBar items={items} />
        {/* Sprint 9: T / Shift+T / L, one listener for every signed-in page (off on /shared/*). */}
        <GlobalShortcuts />
      </TimerActionsProvider>
    </div>
  );
}
